/**
 * JN-ADMIN-IMP-001 (2026-05-27) — Impersonation banner.
 *
 * Rendered above the AppShell content on EVERY authenticated page.
 * Visible only when the access token in localStorage carries the
 * `imp` claim (set by signImpersonationToken on the server).
 *
 * Why a hard-to-miss red bar across the top:
 *
 *   The whole purpose of impersonation is to see what a user sees —
 *   but that means every screen you look at is identical to a real
 *   user's screen. Without an unambiguous, can't-ignore marker the
 *   admin will eventually take an action thinking they're themselves
 *   and the audit trail will record it as the impersonated user.
 *   The banner is non-dismissable; it stays on every page until the
 *   session ends.
 *
 * "End impersonation" button:
 *   1. Fires a best-effort audit event to the API.
 *   2. Clears the tenant-user access token from localStorage.
 *   3. Sends the tab back to /admin/tenants. (The admin's own /admin/*
 *      session in the original tab is unaffected — they still have
 *      their platform_admin_token there.)
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { decodeAccessToken, clearAccessToken } from '../lib/auth';
import { platformAdminEndImpersonation } from '../lib/platformAdminApi';

export function ImpersonationBanner(): JSX.Element | null {
  const navigate = useNavigate();
  const decoded = decodeAccessToken();
  const impersonatedBy = decoded?.imp;
  const impEmail = decoded?.impEmail;
  const targetUserId = decoded?.sub ?? null;
  const targetTenantId = decoded?.tenantId ?? null;

  const handleEnd = useCallback(() => {
    // Best-effort: fire the audit event, then clear the token regardless.
    if (targetUserId && targetTenantId) {
      void platformAdminEndImpersonation({
        targetUserId,
        targetTenantId,
      }).catch(() => {
        // We don't surface this — even if the audit ping fails the
        // local logout below MUST still happen.
      });
    }
    clearAccessToken();
    // Send the tab to /admin so the admin lands on a useful page; if
    // their platform_admin_token has expired they'll be bounced to
    // /admin/login by that route's guard.
    navigate('/admin/tenants', { replace: true });
  }, [navigate, targetUserId, targetTenantId]);

  if (!impersonatedBy) return null;

  const expiresLabel = expiresInLabel(decoded?.exp);

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9000,
        background: 'var(--red)',
        color: 'var(--black)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'var(--red)',
          color: 'var(--black)',
          textAlign: 'center',
          lineHeight: '20px',
          fontSize: 13,
          fontWeight: 800,
        }}
      >
        !
      </span>
      <div style={{ flex: 1, lineHeight: 1.4 }}>
        Impersonation session — every action is taken AS this user and is
        attributed to them in the audit log.
        {impEmail && (
          <>
            {' '}
            Started by{' '}
            <span style={{ fontFamily: 'var(--ff)', fontWeight: 700 }}>
              {impEmail}
            </span>
            .
          </>
        )}
        {expiresLabel && (
          <span style={{ fontWeight: 400, opacity: 0.9 }}>
            {' '}Session ends in {expiresLabel}.
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={handleEnd}
        data-testid="impersonation-end-button"
        style={{
          background: 'white',
          color: 'var(--red-d)',
          border: 'none',
          padding: '6px 14px',
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        End impersonation
      </button>
    </div>
  );
}

function expiresInLabel(expSeconds: number | undefined): string | null {
  if (!expSeconds) return null;
  const remainingMs = expSeconds * 1000 - Date.now();
  if (remainingMs <= 0) return 'now';
  const mins = Math.round(remainingMs / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remMin = mins % 60;
  return remMin === 0 ? `${hours}h` : `${hours}h ${remMin}m`;
}
